import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import ProcurementPackGenerator from "../components/ProcurementPackGenerator";
import {
  Image as ImageIcon,
  QrCode,
  ScrollText,
  FileText,
  ShieldCheck,
  UserCircle2,
  FileCheck2,
  FileBox,
  Download,
  Loader2,
  AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICON = {
  logos: ImageIcon,
  "social-avatars": UserCircle2,
  "qr-codes": QrCode,
  disclaimers: ScrollText,
  "header-spec": FileText,
  "doc-control": ShieldCheck,
  "marketing-issued": FileCheck2,
  templates: FileBox,
};

export default function BrandAssetsPage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPtBr = i18n.language?.startsWith("pt");

  useEffect(() => {
    axios
      .get(`${API}/brand-assets/manifest`, { withCredentials: true })
      .then((res) => setManifest(res.data))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403)
          setError("Access restricted to Admin and Auditor / PM roles.");
        else if (status === 401)
          setError("Session expired. Please sign in again.");
        else setError("Failed to load brand assets.");
      })
      .finally(() => setLoading(false));
  }, []);

  const downloadAsset = (item) => {
    // Force download via fetch + blob to avoid in-tab navigation for .md files.
    fetch(item.url)
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = item.url.split("/").pop();
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="brand-assets-loading">
        <Loader2 className="animate-spin text-[#00C8E0]" size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="max-w-2xl mx-auto mt-12 p-6 rounded border border-[rgba(255,80,80,0.25)] bg-[rgba(255,80,80,0.06)] text-[#FCA5A5] flex items-start gap-3"
        data-testid="brand-assets-error"
      >
        <AlertCircle size={20} />
        <div>
          <p className="text-sm font-medium mb-1">Brand Asset Hub unavailable</p>
          <p className="text-xs text-[#FECACA]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="brand-assets-page">
      {/* Page header */}
      <header className="border-b border-[rgba(255,255,255,0.08)] pb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#00C8E0] font-semibold mb-2">
          {isPtBr ? "Conformidade Administrativa" : "Admin Compliance"}
        </p>
        <h1 className="text-3xl text-white font-light tracking-tight mb-2">
          {isPtBr ? "Hub de Ativos da Marca Charstan" : "Charstan Brand Asset Hub"}
        </h1>
        <p className="text-[#94A3B8] text-sm max-w-3xl">
          {isPtBr
            ? "Repositório controlado de logotipos, códigos QR, avisos legais, especificações de cabeçalho e documentos de controle. Use os ativos canônicos para qualquer documento ou comunicação Charstan. Acesso restrito a Admin e PM/Auditor."
            : "Controlled repository of logos, QR codes, disclaimers, header specifications, and document-control procedures. Use these canonical assets for any Charstan document or communication. Access is restricted to Admin and PM/Auditor roles."}
        </p>
        <div className="flex items-center gap-3 mt-4 text-xs text-[#64748B]">
          <span className="px-2 py-0.5 rounded bg-[rgba(0,200,224,0.1)] border border-[rgba(0,200,224,0.2)] text-[#00C8E0] font-mono">
            DCG-001-EN-{manifest?.version || "v1.0"}
          </span>
          <span>·</span>
          <span>{isPtBr ? "Emitido em" : "Issued"} {manifest?.issued}</span>
          <span>·</span>
          <span>
            {isPtBr ? "Acessando como" : "Accessing as"}{" "}
            <span className="text-[#94A3B8] capitalize">{user?.role}</span>
          </span>
        </div>
      </header>

      {/* CHARSTAN_PR_RUN_005 — Procurement Pack Generator (admin-only widget). */}
      {user?.role === "admin" && <ProcurementPackGenerator isPtBr={isPtBr} />}

      {/* PR_RUN_055 — One-click GTM kit ZIP download (admin-only).
          Bundles 5 locale QR PNGs + 5 sample flyers (all carrying
          CHARSTAN-LAUNCH) + 3 reference Markdown deltas. Useful when
          someone asks "send me the kit". */}
      {user?.role === "admin" && (
        <section
          className="rounded border border-[rgba(201,168,76,0.18)] bg-[rgba(201,168,76,0.04)] p-5 flex flex-col md:flex-row items-start md:items-center gap-4"
          data-testid="charstan-kit-bundle-card"
        >
          <div className="p-3 rounded bg-[rgba(201,168,76,0.12)] border border-[rgba(201,168,76,0.25)]">
            <FileBox className="text-[#C9A84C]" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-sm font-semibold">
              {isPtBr ? "Pacote GTM Charstan (ZIP)" : "Charstan GTM kit bundle (ZIP)"}
            </h3>
            <p className="text-[#8B95A5] text-[12px] mt-1 leading-relaxed">
              {isPtBr
                ? "Inclui 5 QR codes por idioma · 5 flyers de amostra com CHARSTAN-LAUNCH · taxonomia de campanhas · deltas MK-003 v1.1 + HANDOFF v1.2. Gerado sob demanda; sem cache."
                : "Includes 5 locale QR codes · 5 sample flyers carrying CHARSTAN-LAUNCH · campaign-tag taxonomy · MK-003 v1.1 + HANDOFF v1.2 deltas. Generated on demand; no caching."}
            </p>
          </div>
          <a
            href={`${API}/marketing/kit/charstan-kit.zip`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded bg-[#C9A84C] text-[#0D0F14] text-xs font-medium hover:bg-[#D6B663] transition-colors"
            data-testid="charstan-kit-bundle-download-btn"
          >
            <Download size={14} />
            <span>{isPtBr ? "Baixar pacote" : "Download kit"}</span>
          </a>
        </section>
      )}

      {/* Categories */}
      {manifest?.categories?.map((cat) => {
        const Icon = CATEGORY_ICON[cat.category_id] || FileText;
        return (
          <section
            key={cat.category_id}
            className="rounded border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] p-6"
            data-testid={`brand-assets-category-${cat.category_id}`}
          >
            <div className="flex items-start gap-4 mb-5">
              <div className="p-3 rounded bg-[rgba(0,200,224,0.08)] border border-[rgba(0,200,224,0.15)]">
                <Icon className="text-[#00C8E0]" size={20} />
              </div>
              <div className="flex-1">
                <h2 className="text-white text-lg font-medium mb-1">
                  {isPtBr ? cat.label_pt_br : cat.label_en}
                </h2>
                <p className="text-[#94A3B8] text-xs leading-relaxed">
                  {isPtBr ? cat.description_pt_br : cat.description_en}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cat.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 rounded border border-[rgba(255,255,255,0.06)] bg-[rgba(0,0,0,0.2)] hover:border-[rgba(0,200,224,0.3)] transition-colors"
                  data-testid={`brand-asset-${item.id}`}
                >
                  <div className="flex-1 min-w-0 pr-3">
                    <p className="text-white text-sm font-medium truncate">{item.name}</p>
                    <p className="text-[#64748B] text-[11px] font-mono mt-0.5">{item.size_label}</p>
                  </div>
                  <button
                    onClick={() => downloadAsset(item)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#00C8E0] text-[#0A0F1C] text-xs font-medium hover:bg-[#00B4CC] transition-colors"
                    data-testid={`brand-asset-download-${item.id}`}
                  >
                    <Download size={14} />
                    <span>{isPtBr ? "Baixar" : "Download"}</span>
                  </button>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {/* Footer compliance line */}
      <div className="text-center text-[10px] text-[#475569] py-4 border-t border-[rgba(255,255,255,0.04)]">
        {isPtBr
          ? "Hub controlado conforme ISO 9001:2015 §7.5 · ISO 13485:2016 §4.2.4 · 21 CFR Part 11 §11.10. Cópias não controladas devem ser verificadas contra o repositório canônico antes do uso."
          : "Hub controlled per ISO 9001:2015 §7.5 · ISO 13485:2016 §4.2.4 · 21 CFR Part 11 §11.10. Uncontrolled copies must be verified against the canonical repository before use."}
      </div>
    </div>
  );
}
